import { ShieldCheck } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { api, setAccessToken, unwrap } from '@/api/client';
import { ApiError, cuentaRestringidaDe, mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { InputOTP, InputOTPSlot } from '@/components/ui/input-otp';

type StepUpResult = components['schemas']['StepUpResult'];
interface Reto {
  challengeToken: string;
  emailMasked: string;
  resolve: () => void;
  reject: (e: Error) => void;
}

/**
 * Acciones sensibles (cambiar correo o contraseña, eliminar la cuenta): si la API responde
 * `STEP_UP_REQUIRED`, pide el código que llega por correo, renueva el token y reintenta.
 * Uso: `const { proteger, dialogo } = useStepUp(); await proteger(() => accion());` y `{dialogo}`.
 */
export function useStepUp(): {
  proteger: <T>(fn: () => Promise<T>) => Promise<T>;
  dialogo: ReactNode;
} {
  const [reto, setReto] = useState<Reto | null>(null);
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const proteger = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'STEP_UP_REQUIRED')) throw e;
      const r = await unwrap<StepUpResult>(api.POST('/api/v1/auth/step-up'));
      if (r.status === 'verified') {
        setAccessToken(r.accessToken);
        return fn();
      }
      await new Promise<void>((resolve, reject) => {
        setCodigo('');
        setReto({ challengeToken: r.challengeToken, emailMasked: r.emailMasked, resolve, reject });
      });
      return fn();
    }
  }, []);

  const verificar = async (code: string) => {
    if (!reto || code.length !== 6) return;
    setEnviando(true);
    try {
      const r = await unwrap<StepUpResult>(
        api.POST('/api/v1/auth/step-up/verify', {
          body: { challengeToken: reto.challengeToken, code },
        }),
      );
      if (r.status !== 'verified')
        throw new Error('No pudimos verificar el código. Inténtalo de nuevo.');
      setAccessToken(r.accessToken);
      reto.resolve();
      setReto(null);
    } catch (e) {
      // Suspendida o bloqueada: el cliente ya cerró la sesión y el login muestra el modal.
      if (cuentaRestringidaDe(e)) {
        reto.reject(e as Error);
        setReto(null);
        return;
      }
      toast.error(mensajeDe(e));
      setCodigo('');
    } finally {
      setEnviando(false);
    }
  };

  const dialogo = (
    <Dialog
      open={reto !== null}
      onOpenChange={(o) => {
        if (!o && reto) {
          reto.reject(new Error('Cancelaste la verificación.'));
          setReto(null);
        }
      }}
    >
      <DialogContent>
        <ShieldCheck className="text-cian-texto" size={32} />
        <DialogTitle>Confirma que eres tú</DialogTitle>
        <DialogDescription>
          Por seguridad, escribe el código de 6 dígitos que enviamos a{' '}
          <strong>{reto?.emailMasked}</strong>.
        </DialogDescription>
        <div className="my-5 flex justify-center">
          <InputOTP
            maxLength={6}
            value={codigo}
            onChange={setCodigo}
            onComplete={(v: string) => void verificar(v)}
            disabled={enviando}
            autoFocus
            inputMode="numeric"
            pattern="^[0-9]*$"
            aria-label="Código de verificación"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTP>
        </div>
      </DialogContent>
    </Dialog>
  );

  return { proteger, dialogo };
}
